import React, { useState, useEffect } from "react";
import axios from "axios";
import { xml2json } from "xml-js";

const UploadRubric = ({ platformUrl }) => {
    const [rawJsonData, setRawJsonData] = useState(null);
    const [jsonData, setJsonData] = useState([]);
    const [courseId, setCourseId] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadResults, setUploadResults] = useState([]);

    // Extract query parameters
    const params = new URLSearchParams(window.location.search);
    console.log ('params:' + JSON.stringify(window.location.search));

    const token = params.get("token");
    const courseUuid = params.get("courseUuid");

    // Fetch course details using courseUuid
    useEffect(() => {
        const fetchCourseDetails = async () => {
            if (!courseUuid || courseUuid.includes('_1')) return;

            try {
                const response = await axios.get(
                    `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                const fetchedCourseId = response.data.courseId;
                setCourseId(fetchedCourseId);
                setMessage(`Course ID loaded: ${fetchedCourseId}`);
            } catch (err) {
                setError(`Failed to fetch course details: ${err.message}`);
            }
        };

        fetchCourseDetails();
    }, [courseUuid, platformUrl, token]);

    const cleanHtml = (text) => {
        return text ? text.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() : "";
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const xmlText = e.target.result;
                    const rawJson = JSON.parse(xml2json(xmlText, { compact: true, spaces: 2 }));
                    if (!rawJson || !rawJson.rubrics) {
                        throw new Error("Invalid XML structure");
                    }
                    rawJson.rubrics = rawJson.rubrics.rubric;
                    setRawJsonData(rawJson);
                    setMessage("File successfully loaded.");
                    setError("");
                } catch (err) {
                    setError("Error parsing XML file: " + err.message);
                    setRawJsonData(null);
                }
            };
            reader.onerror = () => {
                setError("Error reading file.");
                setRawJsonData(null);
            };
            reader.readAsText(file);
        }
    };

    const transformRubricJson = () => {
        if (!rawJsonData) return;
        try {
            let rubrics = rawJsonData.rubrics;
            if (!Array.isArray(rubrics)) {
                rubrics = [rawJsonData.rubrics];
            }
            console.log ("Rubrics #: " + rubrics.length);
            const transformedRubrics = rubrics.map(rubric => {
                if (!rubric) throw new Error("Invalid rubric structure");
                console.log ('rubric: ' + JSON.stringify(rubric));
                const columnPoints = {};

                let columns = rubric.criteria_groups.criteria_group.level_set.levels.level.map(level => {
                    const levelId = level._attributes["level_id"] || "";
                    const levelPoints = parseFloat(level._attributes["level_value"] || "0");
                    columnPoints[levelId] = levelPoints;
                    return {
                        id: levelId,
                        header: level._attributes["name"] || "",
                        position: parseInt(level._attributes["sort_order"] || "0", 10) - 1
                    };
                }).sort((a, b) => a.position - b.position) || [];

                // Ensure sequential sort_order for columns
                columns = columns.map((col, index) => ({ ...col, position: index }));
                console.log ('columns: ' + JSON.stringify(columns));

                if (!Array.isArray(rubric.criteria_groups.criteria_group.criteria.criterion)) {
                    rubric.criteria_groups.criteria_group.criteria.criterion = [
                        rubric.criteria_groups.criteria_group.criteria.criterion,
                    ];
                }
                let rows = rubric.criteria_groups.criteria_group.criteria.criterion.map(criterion => ({
                    id: criterion._attributes["sort_order"] || "",
                    header: criterion._attributes["name"] || "",
                    position: parseInt(criterion._attributes["sort_order"] || "0", 10) - 1,
                    rowPoints: Math.max(...rubric.criteria_groups.criteria_group.level_set.levels.level.map(l => parseFloat(l._attributes["level_value"] || "0")) || [0])
                })).sort((a, b) => b.position - a.position) || [];

                // Ensure sequential sort_order for rows
                rows = rows.map((row, index) => ({ ...row, position: index }));
                console.log ('rows: ' + JSON.stringify(rows));

                const cells = [];
                rows.forEach(row => {
                    columns.forEach(column => {
                        const matchingCriterion = rubric.criteria_groups.criteria_group.criteria.criterion.find(c => c._attributes["sort_order"] === row.id);
                        const matchingCell = matchingCriterion.cells.cell.find(cell => cell._attributes?.level_id === column.id);

                        cells.push({
                            id: `${column.id}_${row.id}`,
                            rowId: row.id,
                            columnId: column.id,
                            rowPosition: row.position,
                            columnPosition: column.position,
                            points: { value: matchingCell._attributes.cell_value || columnPoints[column.id] || 0 },
                            description: cleanHtml(matchingCell?.description?.text?._text || "")
                        });
                    });
                });

                return {
                    title: rubric._attributes["name"] || "Untitled Rubric",
                    rubricType: "Numeric",
                    columns,
                    rows,
                    cells
                };
            });

            setJsonData(transformedRubrics);
            setRawJsonData(null);
        } catch (error) {
            console.error("Error transforming rubric JSON:", error);
        }
    };

    const handleSendToBlackboard = async () => {
        if (!jsonData.length) {
            setError("No transformed data to send. Please transform first.");
            return;
        }

        if (!courseId) {
            setError("Please enter a course ID.");
            return;
        }

        setLoading(true);
        setMessage("");
        setError("");
        setUploadResults([]);

        try {
            const results = [];
            for (const rubric of jsonData) {
                try {
                    await axios.post(
                        `${platformUrl}/learn/api/public/v1/courses/courseId:${courseId}/rubrics`,
                        rubric,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                    results.push({ rubric: rubric.title, status: "Success" });
                } catch (err) {
                    results.push({ rubric: rubric.title, status: "Failed", error: err.message });
                }
            }
            setUploadResults(results);
            setMessage("Upload process completed.");
        } catch (err) {
            setError("Error sending rubrics to Blackboard.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="upload-rubric-container">
            <h1>D2L to Blackboard Rubrics Migration Tool</h1>
            <p>To use this tool, export the rubrics you need from D2L, load the file here, transform it, and send it to Blackboard.
                When you load the file, you will first see the data from D2L converted from XML to JSON.  When you transform it, you
                will see the data transformed to the structure required for Blackboard.  You can review the data and spot check for any issues.
                Once you send it to Blackboard, you will see which rubrics were successful and which failed.  For those that were successful,
                you will see them in the rubrics area for the course.  For any that failed, you may have to migrate them manually until the tool
                is updated to handle the reason for the failure.
            </p>
            <input type="file" accept=".xml" onChange={handleFileUpload} />
            <button onClick={transformRubricJson} disabled={!rawJsonData}>Transform</button>
            <input
                type="text"
                placeholder="Enter Course ID"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
            />
            <button onClick={handleSendToBlackboard} disabled={loading}>Send to Blackboard</button>
            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}
            {rawJsonData && (
                <div className="raw-json">
                    <h2>Raw JSON Data</h2>
                    <pre className="json-preview">{JSON.stringify(rawJsonData, null, 2)}</pre>
                </div>
            )}
            {uploadResults.length > 0 && (
                <div className="upload-results">
                    <h2>Upload Results</h2>
                    <ul>
                        {uploadResults.map((result, index) => (
                            <li key={index}>
                                <strong>{result.rubric}</strong>: {result.status}
                                {result.status === "Failed" && ` - ${result.error}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {jsonData.length > 0 && jsonData.map((rubric, index) => (
                <pre key={index} className="json-preview">{JSON.stringify(rubric, null, 2)}</pre>
            ))}
        </div>
    );
};

export default UploadRubric;
